import {
  SEARCH_QUERY_MIN_LENGTH,
  TEMPLATE_CATEGORIES,
  boardPath,
  type TemplateGalleryItem,
} from '@molt/shared';
import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import {
  CreateFromTemplateDialog,
  type CreateFromTemplateDialogSubmit,
} from '@/components/board/CreateFromTemplateDialog';
import { TemplateGallery } from '@/components/board/TemplateGallery';
import { announce } from '@/lib/announce';
import { useCreateBoardFromTemplate } from '@/lib/board-templates';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { signInHref, useSession } from '@/lib/session';
import { useTemplateGallery } from '@/lib/template-gallery';
import { workspacesQueryOptions } from '@/lib/workspaces';

type TemplateCategory = NonNullable<TemplateGalleryItem['templateCategory']>;

interface TemplatesSearch {
  category?: TemplateCategory;
  q?: string;
}

function categoryFrom(value: unknown): TemplateCategory | undefined {
  return typeof value === 'string' &&
    TEMPLATE_CATEGORIES.includes(value as TemplateCategory)
    ? (value as TemplateCategory)
    : undefined;
}

function queryFrom(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length < SEARCH_QUERY_MIN_LENGTH) return undefined;
  return trimmed;
}

function searchHref(search: TemplatesSearch): string {
  const params = new URLSearchParams();
  if (search.category !== undefined) params.set('category', search.category);
  if (search.q !== undefined) params.set('q', search.q);
  const query = params.toString();
  return query === '' ? '/templates' : `/templates?${query}`;
}

export const Route = createFileRoute('/templates')({
  validateSearch: (search: Record<string, unknown>): TemplatesSearch => {
    const category = categoryFrom(search.category);
    const q = queryFrom(search.q);
    return {
      ...(category === undefined ? {} : { category }),
      ...(q === undefined ? {} : { q }),
    };
  },
  component: TemplatesRoute,
});

function TemplatesRoute(): ReactElement {
  const search = Route.useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const { t } = useInterfaceLanguage();
  const session = useSession();
  const signedIn = session.profile !== null;

  const [draftQuery, setDraftQuery] = useState(search.q ?? '');
  const [createTemplate, setCreateTemplate] = useState<TemplateGalleryItem | null>(null);
  const [createResult, setCreateResult] = useState<{
    readonly cardsCopied: number;
    readonly path: string;
  } | null>(null);

  const gallery = useTemplateGallery({
    category: search.category ?? null,
    q: search.q ?? null,
  });
  const workspaces = useQuery({
    ...workspacesQueryOptions(),
    enabled: signedIn && createTemplate !== null,
  });
  const createBoard = useCreateBoardFromTemplate();

  useEffect(() => {
    setDraftQuery(search.q ?? '');
  }, [search.q]);

  const selectedCategory = search.category ?? 'all';
  const templates = gallery.data?.templates ?? [];
  const galleryError = gallery.error?.message;

  const currentSearch = useMemo<TemplatesSearch>(() => {
    const next: TemplatesSearch = {};
    if (search.category !== undefined) next.category = search.category;
    if (search.q !== undefined) next.q = search.q;
    return next;
  }, [search.category, search.q]);

  function replaceSearch(next: TemplatesSearch): void {
    router.history.replace(searchHref(next));
    router.history.flush();
  }

  function handleSearch(nextQuery: string): void {
    const q = queryFrom(nextQuery);
    const next: TemplatesSearch = { ...currentSearch };
    if (q === undefined) {
      delete next.q;
    } else {
      next.q = q;
    }
    replaceSearch(next);
  }

  function handleSelectCategory(category: TemplateCategory | 'all'): void {
    const next: TemplatesSearch = { ...currentSearch };
    if (category === 'all') {
      delete next.category;
    } else {
      next.category = category;
    }
    replaceSearch(next);
  }

  function openCreate(template: TemplateGalleryItem): void {
    if (!signedIn) {
      void navigate({
        href: signInHref({
          pathname: location.pathname,
          search: location.searchStr,
        }),
      });
      return;
    }
    setCreateResult(null);
    setCreateTemplate(template);
  }

  function closeCreate(): void {
    setCreateTemplate(null);
    setCreateResult(null);
    createBoard.reset();
  }

  function handleCreate(input: CreateFromTemplateDialogSubmit): void {
    if (createTemplate === null) return;
    createBoard.mutate(
      {
        templateBoardId: createTemplate.id,
        templateTitle: createTemplate.title,
        workspaceId: input.workspaceId,
        visibility: input.visibility,
        title: input.title,
        keepCards: input.keepCards,
      },
      {
        onSuccess: (result) => {
          announce(
            t('boardTemplates.route.createdAnnounce', { title: result.board.title }),
          );
          setCreateResult({
            cardsCopied: result.cardsCopied,
            path: boardPath(result.board.shortLink, result.board.slug),
          });
        },
      },
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-semibold text-ink">
          {t('boardTemplates.route.heading')}
        </h1>
        <p className="max-w-3xl text-sm text-ink-muted">
          {t('boardTemplates.route.intro')}
        </p>
      </header>

      <TemplateGallery
        templates={templates}
        query={draftQuery}
        selectedCategory={selectedCategory}
        loading={gallery.isPending}
        error={galleryError}
        onQueryChange={setDraftQuery}
        onSearch={handleSearch}
        onSelectCategory={handleSelectCategory}
        onPreview={(template) => void navigate({ href: template.path })}
        onCreate={openCreate}
      />

      {createTemplate === null ? null : (
        <CreateFromTemplateDialog
          open
          templateTitle={createTemplate.title}
          workspaces={workspaces.data ?? []}
          defaultWorkspaceId={createTemplate.workspaceId}
          loading={workspaces.isPending}
          busy={createBoard.isPending}
          cardsCopied={createResult?.cardsCopied}
          createdBoardPath={createResult?.path}
          onCancel={closeCreate}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}
